import React, { useState, useMemo } from 'react';
import { useApp } from '../../contexts/AppContext';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Box,
  Grid,
  Chip,
  Avatar,
  Tooltip,
  Collapse,
  IconButton,
  Tabs,
  Tab,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment
} from '@mui/material';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip as ChartTooltip, 
  Legend, 
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Group as GroupIcon,
  AttachMoney as MoneyIcon,
  TrendingDown as TrendingDownIcon,
  Search as SearchIcon
} from '@mui/icons-material';

// Complexity configuration (aligned with PolicyReviewPage)
const complexityConfig = {
  simple: {
    label: 'Simple',
    weight: 1.0,
    description: 'No accidents in 6 years, no tickets, local driving'
  },
  moderate: {
    label: 'Moderate',
    weight: 1.3,
    description: 'Has accidents/tickets, standard docs'
  },
  complex: {
    label: 'Complex',
    weight: 1.8,
    description: 'Multiple issues, LOE required, complex docs'
  }
};

// Generate mock data with complexity distribution
const generateUnderwriterData = () => {
  const underwriters = ['John Smith', 'Emma Wilson', 'Michael Brown', 'Sarah Lee', 'David Chen'];
  
  return underwriters.map(name => {
    const simple = Math.floor(Math.random() * 20 + 5);
    const moderate = Math.floor(Math.random() * 15 + 3);
    const complex = Math.floor(Math.random() * 10 + 2);
    const totalPolicies = simple + moderate + complex;
    
    // Calculate weighted efficiency score
    const totalWeight = (simple * 1) + (moderate * 1.3) + (complex * 1.8);
    const avgProcessingHours = (Math.random() * 6 + 4).toFixed(1);
    const efficiencyScore = Math.min(100, Math.round(
      (totalWeight * 100) / totalPolicies - (avgProcessingHours * 0.8)
    ));

    return {
      name,
      avatar: name[0],
      totalPolicies,
      annualCases: totalPolicies * 12,
      efficiencyScore,
      avgProcessingHours,
      complexityDistribution: { simple, moderate, complex }
    };
  });
};

const EfficiencyProgress = ({ value }) => (
  <Box sx={{ display: 'flex', alignItems: 'center' }}>
    <LinearProgress 
      variant="determinate" 
      value={value} 
      sx={{ 
        width: '100px',
        height: '8px',
        borderRadius: '4px',
        mr: 1,
        '& .MuiLinearProgress-bar': {
          borderRadius: '4px',
          backgroundColor: value > 80 ? '#4caf50' : 
                         value > 60 ? '#ffb74d' : '#f44336'
        }
      }}
    />
    <Typography variant="body2">{value}%</Typography>
  </Box>
);

const ComplexityChip = ({ level }) => {
  const config = complexityConfig[level];
  return (
    <Tooltip title={config.description}>
      <Chip 
        label={config.label}
        size="small"
        variant="outlined"
        sx={{ 
          borderColor: level === 'simple' ? '#4caf50' :
                     level === 'moderate' ? '#ffb74d' : '#f44336',
          color: level === 'simple' ? '#4caf50' :
                level === 'moderate' ? '#ffb74d' : '#f44336'
        }}
      />
    </Tooltip>
  );
};

// 生成分行数据
const generateBranchData = () => {
  const branches = [
    {
      id: 'all',
      name: 'All Branches',
      location: 'Company Wide'
    },
    {
      id: 'downtown',
      name: 'Downtown Branch',
      location: 'Vancouver Downtown'
    },
    {
      id: 'richmond',
      name: 'Richmond Branch',
      location: 'Richmond Center'
    },
    {
      id: 'burnaby',
      name: 'Burnaby Branch',
      location: 'Metrotown'
    }
  ];

  return branches.map(branch => {
    if (branch.id === 'all') {
      return {
        ...branch,
        metrics: {
          monthlyPolicies: 450,
          annualPolicies: 450 * 12,
          avgProcessingTime: 5.2,
          totalBrokers: 15
        }
      };
    }

    // 为每个分行生成独立数据
    let monthlyPolicies, totalBrokers;
    switch(branch.id) {
      case 'downtown':
        monthlyPolicies = 180;
        totalBrokers = 6;
        break;
      case 'richmond':
        monthlyPolicies = 120;
        totalBrokers = 4;
        break;
      case 'burnaby':
        monthlyPolicies = 150;
        totalBrokers = 4;
        break;
      default:
        monthlyPolicies = 100;
        totalBrokers = 3;
    }

    return {
      ...branch,
      metrics: {
        monthlyPolicies,
        annualPolicies: monthlyPolicies * 12,
        avgProcessingTime: parseFloat((Math.random() * 2 + 4).toFixed(1)), // 生成4.0-6.0之间的随机数
        totalBrokers
      }
    };
  });
};

const UwstatisticsPage = () => {
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [timeRange, setTimeRange] = useState('monthly');
  const [searchTerm, setSearchTerm] = useState('');
  const branchesData = generateBranchData();
  const currentBranch = branchesData.find(b => b.id === selectedBranch);
  const underwriters = generateUnderwriterData();
  const [expandGuide, setExpandGuide] = useState(false);

  const COLORS = ['#1976d2', '#4caf50', '#ff9800', '#e91e63', '#9c27b0'];

  // 根据时间范围生成数据
  const generateMonthlyData = () => {
    const monthCount = timeRange === 'monthly' ? 12 : 
                      timeRange === 'quarterly' ? 4 : 1;
    
    return Array.from({ length: monthCount }, (_, i) => ({
      month: timeRange === 'monthly' ? 
        new Date(2024, i).toLocaleString('en-US', { month: 'short' }) :
        timeRange === 'quarterly' ? 
        `Q${i + 1}` : '2024',
      approvals: 80 + Math.floor(Math.random() * 40)
    }));
  };

  // 生成核保人分布数据
  const generateUWDistributionData = () => {
    const total = underwriters.slice(0,5).reduce((sum, uw) => sum + uw.totalPolicies, 0);
    return underwriters.slice(0,5).map(uw => ({
      name: uw.name,
      value: Math.round((uw.totalPolicies / total) * 100)
    }));
  };

  // 过滤核保人数据
  const filteredUnderwriters = useMemo(() => {
    return underwriters.filter(uw => 
      uw.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [underwriters, searchTerm]);

  // 修改分支选择处理函数
  const handleBranchChange = (event) => {
    setSelectedBranch(event.target.value);
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 4 }}>
        {/* 标题部分 */}
        <Typography variant="h4" sx={{ 
          color: '#1976d2', 
          mb: 3,
          fontWeight: 600,
        }}>
          Underwriting Performance Dashboard
        </Typography>

        {/* 分支选择、时间范围和搜索框 */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <FormControl 
              fullWidth 
              variant="outlined"
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#fff',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976d2',
                  },
                }
              }}
            >
              <InputLabel>Select Branch</InputLabel>
              <Select
                value={selectedBranch}
                onChange={handleBranchChange}
                label="Select Branch"
              >
                {branchesData.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    {branch.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Time Range</InputLabel>
              <Select
                value={timeRange}
                label="Time Range"
                onChange={(e) => setTimeRange(e.target.value)}
              >
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="quarterly">Quarterly</MenuItem>
                <MenuItem value="annual">Annual</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="medium"
              placeholder="Search underwriters..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#9e9e9e' }} />
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#fff',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976d2',
                  },
                }
              }}
            />
          </Grid>
        </Grid>

        {/* Key Metrics Cards */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              height: '100%',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 3
              }
            }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <AssessmentIcon sx={{ color: '#1976d2', fontSize: '1.2rem', mr: 1 }} />
                  <Typography variant="subtitle2" color="textSecondary">
                    Annual Submitted
                  </Typography>
                </Box>
                <Typography variant="h5">{currentBranch.metrics.annualPolicies}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              height: '100%',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 3
              }
            }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <AssessmentIcon sx={{ color: '#4caf50', fontSize: '1.2rem', mr: 1 }} />
                  <Typography variant="subtitle2" color="textSecondary">
                    Monthly Submitted
                  </Typography>
                </Box>
                <Typography variant="h5">{currentBranch.metrics.monthlyPolicies}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              height: '100%',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 3
              }
            }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <TrendingUpIcon sx={{ color: '#4caf50', fontSize: '1.2rem', mr: 1 }} />
                  <Typography variant="subtitle2" color="textSecondary">
                    Avg Time
                  </Typography>
                </Box>
                <Typography variant="h5">{currentBranch.metrics.avgProcessingTime}h</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              height: '100%',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 3
              }
            }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <GroupIcon sx={{ color: '#ff9800', fontSize: '1.2rem', mr: 1 }} />
                  <Typography variant="subtitle2" color="textSecondary">
                    Underwriters
                  </Typography>
                </Box>
                <Typography variant="h5">{currentBranch.metrics.totalBrokers}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* 可折叠的复杂度指南 */}
        <Paper elevation={0} sx={{ 
          p: 2, 
          mb: 4, 
          border: '1px solid #e0e0e0',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: '#f8f9fa'
          }
        }}>
          <Box 
            onClick={() => setExpandGuide(!expandGuide)}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <IconButton size="small" sx={{ mr: 1 }}>
              <ExpandMoreIcon sx={{ 
                transform: expandGuide ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.3s'
              }}/>
            </IconButton>
            <Typography variant="h6" sx={{ color: '#1976d2' }}>
              Case Complexity Classification
            </Typography>
          </Box>
          
          <Collapse in={expandGuide}>
            <Grid container spacing={2} sx={{ mt: 2 }}>
              {/* Simple Cases */}
              <Grid item xs={12} md={4}>
                <Box sx={{ 
                  p: 2,
                  borderLeft: '4px solid #4caf50',
                  bgcolor: '#fff',
                  borderRadius: 1,
                  boxShadow: 1
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Chip 
                      label="Simple" 
                      size="small" 
                      color="success" 
                      variant="outlined"
                      sx={{ mr: 1.5 }}
                    />
                    <Typography variant="subtitle2">Low Risk Profile</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    • No claims in 6+ years
                    <br />
                    • Clean driving record
                    <br />
                    • Local jurisdiction
                  </Typography>
                </Box>
              </Grid>

              {/* Moderate Cases */}
              <Grid item xs={12} md={4}>
                <Box sx={{ 
                  p: 2,
                  borderLeft: '4px solid #ffb74d',
                  bgcolor: '#fff',
                  borderRadius: 1,
                  boxShadow: 1 
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Chip 
                      label="Moderate" 
                      size="small" 
                      color="warning" 
                      variant="outlined"
                      sx={{ mr: 1.5 }}
                    />
                    <Typography variant="subtitle2">Standard Risk</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    • Minor infractions
                    <br />
                    • Single claim history
                    <br />
                    • Standard documents
                  </Typography>
                </Box>
              </Grid>

              {/* Complex Cases */}
              <Grid item xs={12} md={4}>
                <Box sx={{ 
                  p: 2,
                  borderLeft: '4px solid #f44336',
                  bgcolor: '#fff',
                  borderRadius: 1,
                  boxShadow: 1
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Chip 
                      label="Complex" 
                      size="small" 
                      color="error" 
                      variant="outlined"
                      sx={{ mr: 1.5 }}
                    />
                    <Typography variant="subtitle2">High Complexity</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    • Multiple claims
                    <br />
                    • Cross-border exposure
                    <br />
                    • LOE required
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Collapse>
        </Paper>

        {/* 新增图表容器 */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* 月度审核柱状图 */}
          <Grid item xs={12} md={8}>
            <Paper elevation={3} sx={{ p: 3, height: 400 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
                Monthly Policy Approvals
              </Typography>
              <ResponsiveContainer width="100%" height="90%">
                <BarChart data={generateMonthlyData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="month" 
                    tick={{ fill: '#666' }}
                  />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar 
                    dataKey="approvals" 
                    name="Approved Policies"
                    fill="#1976d2"
                    barSize={20}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* 核保人占比饼图 */}
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 3, height: 400 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
                Underwriter Distribution
              </Typography>
              <ResponsiveContainer width="100%" height="90%">
                <PieChart>
                  <Pie
                    data={generateUWDistributionData()}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {generateUWDistributionData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Legend 
                    layout="vertical"
                    align="right"
                    verticalAlign="middle"
                  />
                  <Tooltip 
                    formatter={(value, name) => [`${value}%`, name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>

        {/* Underwriter Performance Table - 使用过滤后的数据 */}
        <Paper elevation={0} sx={{ p: 3, mb: 4 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 3 
          }}>
            <Typography variant="h6" sx={{ 
              color: '#424242',
              fontWeight: 600
            }}>
              Underwriter Performance
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Total Results: {filteredUnderwriters.length}
            </Typography>
          </Box>

          {filteredUnderwriters.length === 0 ? (
            <Box sx={{ 
              textAlign: 'center', 
              py: 4,
              bgcolor: '#f8f9fa',
              borderRadius: 1
            }}>
              <Typography color="textSecondary">
                No underwriters found matching your search criteria
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table sx={{
                '& .MuiTableCell-root': {
                  color: '#212121',
                  borderBottom: 'none'
                }
              }}>
                <TableHead>
                  <TableRow sx={{
                    '& th': {
                      fontWeight: 600,
                      fontSize: '0.875rem'
                    }
                  }}>
                    <TableCell>Underwriter</TableCell>
                    <TableCell align="center">Annual Policies</TableCell>
                    <TableCell align="center">Monthly Policies</TableCell>
                    <TableCell align="center">Simple</TableCell>
                    <TableCell align="center">Moderate</TableCell>
                    <TableCell align="center">Complex</TableCell>
                    <TableCell align="center">Avg Time</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUnderwriters.map((uw) => (
                    <TableRow 
                      key={uw.name} 
                      hover
                      sx={{
                        '&:nth-of-type(even)': {
                          backgroundColor: '#fafafa'
                        }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ 
                            mr: 2,
                            bgcolor: '#e3f2fd', 
                            color: '#1976d2',
                            width: 32,
                            height: 32
                          }}>
                            {uw.avatar}
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {uw.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {Math.floor(uw.totalPolicies * 12)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ 
                          display: 'inline-flex',
                          alignItems: 'center',
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1
                        }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {uw.totalPolicies}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        {uw.complexityDistribution.simple}
                      </TableCell>
                      <TableCell align="center">
                        {uw.complexityDistribution.moderate}
                      </TableCell>
                      <TableCell align="center">
                        {uw.complexityDistribution.complex}
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {uw.avgProcessingHours}h
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default UwstatisticsPage; 